import React, { useState, useEffect } from 'react';
import { Edit3, Copy, Eye, DollarSign } from 'lucide-react';
import StatusBadge from './StatusBadge';

const TimeEntryCell = ({ entry, project, client, onEdit, onDuplicate, onView, onStatusUpdate, isCompact = false }) => {
  const [showActions, setShowActions] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editValues, setEditValues] = useState({
    task_name: entry.task_name,
    duration_hours: entry.duration_hours,
    description: entry.description || '',
    is_billable: entry.is_billable,
    user_id: entry.user_id
  });
  
  const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setShowEditModal(true);
  };

  const handleEditSave = () => {
    onEdit(entry.id, editValues);
    setShowEditModal(false);
  };

  const handleEditCancel = () => {
    setEditValues({
      task_name: entry.task_name,
      duration_hours: entry.duration_hours,
      description: entry.description || '',
      is_billable: entry.is_billable,
      user_id: entry.user_id
    });
    setShowEditModal(false);
  };

  useEffect(() => {
    if (showEditModal) {
      setEditValues({
        task_name: entry.task_name,
        duration_hours: entry.duration_hours,
        description: entry.description || '',
        is_billable: entry.is_billable,
        user_id: entry.user_id
      });
    }
  }, [showEditModal, entry]);

  return (
    <>
      <div 
        className={`group relative ${isCompact ? 'p-1 mb-1' : 'p-2 mb-2'} rounded-lg border-l-2 hover:shadow-sm transition-all cursor-pointer overflow-hidden`}
        style={{ 
          borderLeftColor: project?.color || '#6B7280',
          backgroundColor: project?.color ? `${project.color}08` : '#F9FAFB'
        }}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        <div className="flex items-start justify-between min-h-0">
          <div className={`flex-1 min-w-0 ${isCompact ? 'space-y-0' : 'space-y-1'}`}>
            <div className={`font-medium text-slate-800 truncate ${isCompact ? 'text-xs leading-tight' : 'text-sm'}`}>
              {entry.task_name}
            </div>
            {!isCompact && (
              <div className="text-xs text-slate-600 truncate">
                {project?.name} • {client?.name}
              </div>
            )}
            <div className="flex items-center space-x-1 flex-wrap">
              <span className={`text-xs font-medium ${isCompact ? 'text-slate-600' : 'text-slate-700'}`}>
                {formatTime(entry.duration_hours)}
              </span>
              {entry.is_billable && (
                <DollarSign className="w-3 h-3 text-green-600 flex-shrink-0" />
              )}
              <div className="flex-shrink-0">
                <StatusBadge status={entry.status} />
              </div>
            </div>
          </div>
          
          {showActions && !isCompact && (
            <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity ml-1 flex-shrink-0">
              <button
                onClick={handleEditClick}
                className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                title="Edit"
              >
                <Edit3 className="w-3 h-3" />
              </button>
              <button
                onClick={(e) => { 
                  e.stopPropagation(); 
                  onDuplicate(entry); 
                }}
                className="p-1 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded"
                title="Duplicate"
              >
                <Copy className="w-3 h-3" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onView && onView(entry);
                }}
                className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                title="View entry details"
              >
                <Eye className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              Edit Time Entry
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Task Name</label>
                <input
                  type="text"
                  value={editValues.task_name}
                  onChange={(e) => setEditValues(prev => ({ ...prev, task_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Duration (hours)</label>
                <input
                  type="number"
                  step="0.25"
                  value={editValues.duration_hours}
                  onChange={(e) => setEditValues(prev => ({ ...prev, duration_hours: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={editValues.description}
                  onChange={(e) => setEditValues(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="editBillableCalendar"
                  checked={editValues.is_billable}
                  onChange={(e) => setEditValues(prev => ({ ...prev, is_billable: e.target.checked }))}
                  className="mr-2 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="editBillableCalendar" className="text-sm text-slate-700">
                  Billable
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleEditCancel}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TimeEntryCell;